import { isArray, sortBy } from 'lodash'
import React, { useCallback, useMemo, useRef } from 'react'
import { Platform, StyleSheet, useWindowDimensions, View } from 'react-native'
import { useRouter } from 'expo-router'
import { useSafeAreaInsets } from 'react-native-safe-area-context'
import { BottomSheetBackdrop, BottomSheetModal, BottomSheetScrollView } from '@gorhom/bottom-sheet'
import { DSColor } from '@ppwr/poker'
import { useMutation } from 'convex/react'
import { api } from '@ppwr/convex/_generated/api'
import type { Id } from '@ppwr/convex/_generated/dataModel'

import { DS } from '../../../../design-system'
import { useStore } from '../../../../models'
import { LiveBadge } from './LiveBadge'
import { ReduceMotion } from 'react-native-reanimated'
import { useMaxWidth } from '@/components/WebWrapper/WebWrapper'
import Toast from 'react-native-toast-message'
import { events } from '@/models/analytics/analytics-events'
import { isMaintenanceModeEnabled, showMaintenanceModeAlert } from '@/screens/play-screen/maintenanceMode'

interface GameInfoProps {
  textColor: DSColor
  isCompleted?: boolean
  nBeginnerTables?: number
  nAdvancedTables?: number
  maxPlayersN?: number | [number, number]
  multipleBlindOptions?: boolean
  winners?: string[]
}

const GameInfo: React.FC<GameInfoProps> = ({
  isCompleted,
  nBeginnerTables,
  nAdvancedTables,
  textColor: textColorProp,
  maxPlayersN,
  winners
}) => {
  const styles = GameInfoStyles
  const textColor = DS.palette[textColorProp]
  const getMaxPlayersText = () => {
    if (isArray(maxPlayersN)) {
      return `${maxPlayersN[0]} - ${maxPlayersN[1]}`
    }
    return maxPlayersN
  }
  if (isCompleted) {
    return (
      <DS.Frame gap="8" style={[styles.gameInfoFrame, { borderColor: textColor }]}>
        {winners?.length ? (
          winners.map((name) => (
            // eslint-disable-next-line jsx-a11y/accessible-emoji
            <DS.Text key={name} variant="Body Medium/Regular" color="Global/White">
              👑 {name}
            </DS.Text>
          ))
        ) : (
          <DS.Text variant="Body Small/Regular" color="Global/White">
            No winners
          </DS.Text>
        )}
      </DS.Frame>
    )
  }
  const nTables = (nBeginnerTables || 0) + (nAdvancedTables || 0)
  return (
    <DS.Frame gap="8" style={[styles.gameInfoFrame, { borderColor: textColor }]}>
      <DS.Frame gap="8" row>
        <DS.Icon name="Chip" size={16} color="Global/White" />
        <DS.Text variant="Body Small/Regular" color="Global/White">
          {nTables} tables
        </DS.Text>
      </DS.Frame>
      <DS.Frame gap="8" row>
        <DS.Icon name="Person" size={16} color="Global/White" />
        <DS.Text variant="Body Small/Regular" color="Global/White">
          {getMaxPlayersText()} Players per table
        </DS.Text>
      </DS.Frame>
    </DS.Frame>
  )
}

const GameInfoStyles = StyleSheet.create({
  gameInfoFrame: {
    borderTopWidth: 1,
    borderColor: DS.palette['Brand/Pink'],
    paddingTop: DS.spacing[12]
  }
})

const Icons = {
  live: <DS.Icon name="Person--filled" size={16} color={'Brand/Orange'} />,
  upcoming: <DS.Icon name="Person--filled" size={16} color={'Brand/Orange'} />,
  finished: <DS.Icon name="Check--enclosed" size={16} color={'Global/Black'} />
}

interface AdvancedGameCardProps {
  tableName?: string
  channelName?: string
  actionTime?: number
  blinds?: readonly number[]
  seats?: readonly number[]
  isFull?: boolean
  isLocked?: boolean
  onJoin?: (channelName: string) => Promise<void>
  scheduleId?: string
  tableType?: 'beginner' | 'main'
  tableConfigIndex?: number
  tableNumber?: number
  isVirtual?: boolean
}

export const AdvancedGameCard: React.FC<AdvancedGameCardProps> = ({
  actionTime,
  blinds = [],
  seats = [],
  channelName,
  isLocked,
  isFull,
  onJoin,
  tableName
}) => {
  const buttonText = 'Join Table'
  const seatsText = isFull ? 'Full' : `${seats[1] - seats[0]} Seats Available`
  const notLockedText = `${seats[0]}/${seats[1]} • ${seatsText}`
  const lockedText = 'Window to play ended'
  const statusText = isLocked ? lockedText : seatsText

  const {
    analytics: { setEvent },
    session: { user }
  } = useStore()

  const handleJoin = async () => {
    if (onJoin) {
      setEvent(`${tableName} tapped`, {
        userId: user?._id.toString()
      })
      await onJoin(channelName || '')
    }
  }

  return (
    <DS.Frame
      bg="Global/White"
      gap="16"
      style={{
        paddingHorizontal: DS.spacing[16],
        paddingVertical: DS.spacing[12],
        borderRadius: DS.spacing[16],
        flex: 1,
        flexBasis: '33%'
      }}
    >
      <DS.Frame gap="8">
        <DS.Text variant="Heading 5/Medium">{tableName}</DS.Text>
        <DS.Text variant="Caption/Regular">Actions {actionTime} sec.</DS.Text>
        <DS.Text variant="Caption/Regular">
          Blinds {blinds[0]}/{blinds[1]}
        </DS.Text>
        <DS.Frame gap="6" row>
          <DS.Icon name="Person" color="Brand/Orange" size={12} />
          <DS.Text variant="Caption/Regular">{statusText}</DS.Text>
        </DS.Frame>
      </DS.Frame>
      <DS.Button variant="Medium/Primary" type="On Light" onPress={handleJoin}>
        {buttonText}
      </DS.Button>
    </DS.Frame>
  )
}

interface CardLiveGameProps {
  type?: 'live' | 'upcoming' | 'finished'
  time?: Date
  nowPlaying?: number
  nBeginnerTables?: number
  nAdvancedTables?: number
  minPlayersN?: number
  maxPlayersN?: number | [number, number]
  multipleBlindOptions?: boolean
  isLocked?: boolean
  isFull?: boolean
  onReminderPress?: (dayOfWeek?: number) => void
  onBeginnerPress?: () => void
  advancedTables?: AdvancedGameCardProps[]
  isSetReminderSelected?: boolean
  winners?: string[]
  scheduleId?: string
  tableType?: 'beginner' | 'main'
}

const themes = {
  default: {
    bgColor: 'Brand/Maroon',
    textColor: 'Brand/Pink'
  },
  tomorrow: {
    bgColor: 'Shades/Maroon 2',
    textColor: 'Brand/Orange'
  }
} as const

const isWeb = Platform.OS === 'web'
const ENABLE_NOTIFICATIONS = !isWeb

export const CardLiveGame: React.FC<CardLiveGameProps> = ({
  type,
  isFull,
  isLocked,
  nowPlaying,
  time,
  maxPlayersN,
  minPlayersN,
  multipleBlindOptions,
  nAdvancedTables,
  nBeginnerTables,
  onBeginnerPress,
  onReminderPress,
  advancedTables,
  isSetReminderSelected,
  winners,
  scheduleId,
  tableType
}) => {
  const router = useRouter()
  const {
    session,
    analytics: { setEvent }
  } = useStore()

  const joinScheduledTable = useMutation(api.gameSchedules.mutations.joinScheduledTable)
  const joinGameMutation = useMutation(api.games.mutations.joinGame)
  // const joinGameAsObserverMutation = useMutation(api.games.mutations.joinGameAsObserver)
  const spectateGameMutation = useMutation(api.games.mutations.spectateGame)
  const mainTablesSheetRef = useRef<BottomSheetModal>(null)
  const windowDimensions = useWindowDimensions()
  const maxWidth = useMaxWidth()
  const maxHeight = windowDimensions.height * 0.85

  const isPlural = (nAdvancedTables || 0) + (nBeginnerTables || 0) > 1
  const hasTableWithSevenPlayers = !!advancedTables?.filter(({ seats }) => seats && seats[1] === 7).length
  const oneMainTableWith7Players = hasTableWithSevenPlayers && advancedTables?.length === 1
  const hasTableWithNinePlayers = (!oneMainTableWith7Players && advancedTables?.length !== 0) || nBeginnerTables !== 0

  const now = new Date()
  const isTomorrow = now.getDay() < (time?.getDay() || 0)
  const formattedDate = time?.toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: true
  })

  const observeBeginnerTable = isLocked || isFull
  const observeAdvancedTable = advancedTables?.every((game) => game.isFull || game.isLocked)

  // Show Join only when live and inside join window (not locked); upcoming shows only Set Reminder
  const buttonShouldBeVisible = type === 'live' && !isLocked && !isFull && (nBeginnerTables === 0 || !!onBeginnerPress)
  const theme = themes[isTomorrow ? 'tomorrow' : 'default']
  const styles = CardLiveGameStyles

  const gameStatusText = useMemo(() => {
    if (type === 'finished') {
      return isPlural ? 'Games finished' : 'Game finished'
    }
    if (type === 'upcoming' || isLocked) {
      // If locked (not in pregame window), show upcoming message
      return 'Get notified below'
    }
    if (type === 'live' && isFull) {
      return 'All tables are full'
    }
    if (type === 'live' && (nowPlaying ?? 0) > 0) {
      return `${nowPlaying} Playing now!`
    }
    if (type === 'live') {
      return 'Games are now live!'
    }
    return `${nowPlaying ?? 0} Playing now!`
  }, [isFull, nowPlaying, type, isPlural, isLocked])

  const renderBackdrop = useCallback(
    (props: React.ComponentProps<typeof BottomSheetBackdrop>) => (
      <BottomSheetBackdrop {...props} pressBehavior={'close'} disappearsOnIndex={-1} appearsOnIndex={0} />
    ),
    []
  )

  // Use schedule-derived range when provided (Convex); otherwise infer from table seats for legacy
  const getGameInfoMaxPlayers = (): number | [number, number] => {
    if (maxPlayersN !== undefined && maxPlayersN !== null) {
      return maxPlayersN as number | [number, number]
    }
    if (type === 'upcoming') return 10
    if (!hasTableWithSevenPlayers) return 10
    if (!hasTableWithNinePlayers) return 7
    return [7, 10]
  }

  // useEffect(() => {
  //   bottomSheetModalRef.current?.dismiss()
  // }, [advancedTables?.length])

  const handleBeginnerPress = useCallback(() => {
    if (onBeginnerPress) {
      if (!observeBeginnerTable) {
        setEvent(events.homescreen.play_to_win_tapped, {
          userId: session.user?._id.toString()
        })
      }
      onBeginnerPress()
    }
  }, [
    onBeginnerPress,
    observeBeginnerTable,
    isLocked,
    setEvent,
    session.user?._id,
    type,
    nBeginnerTables,
    scheduleId,
    tableType
  ])

  return (
    <DS.Frame bg={theme.bgColor} gap="16" style={styles.container}>
      <DS.Frame hidden={type !== 'live' || isLocked} style={styles.liveBadge}>
        <LiveBadge />
      </DS.Frame>
      <DS.Frame gap="8">
        <DS.Text variant="Caption/Medium" color={theme.textColor}>
          {isTomorrow ? 'Tomorrow' : 'Community Game'}
        </DS.Text>
        <DS.Text variant="Heading 2/Medium" color="Global/White">
          {formattedDate} {type === 'finished' ? 'Winners' : ''}
        </DS.Text>
        <DS.Frame gap="6" row>
          {type && Icons[type]}
          <DS.Text variant="Body Small/Regular" color="Global/White">
            {gameStatusText}
          </DS.Text>
        </DS.Frame>
      </DS.Frame>
      <GameInfo
        textColor={theme.textColor}
        maxPlayersN={getGameInfoMaxPlayers()}
        multipleBlindOptions={multipleBlindOptions}
        nAdvancedTables={nAdvancedTables}
        nBeginnerTables={nBeginnerTables}
        isCompleted={type === 'finished'}
        winners={winners}
      />
      <DS.Frame hidden={type !== 'live' || isLocked} gap="8" alignItems="center">
        {(nBeginnerTables ?? 0) > 0 ? (
          <DS.Button
            onPress={() => {
              if (isLocked) return
              handleBeginnerPress()
            }}
            variant="Medium/Primary"
            type="On Dark"
            maxWidth
          >
            {observeBeginnerTable ? 'Observe Beginner Table' : 'Join Beginner Table'}
          </DS.Button>
        ) : (
          <DS.Frame style={{ height: 38 }} />
        )}
        {(nAdvancedTables ?? 0) > 0 ? (
          <DS.Button
            onPress={async () => {
              if (isLocked) return
              if (isMaintenanceModeEnabled()) {
                showMaintenanceModeAlert()
                return
              }
              if (!observeAdvancedTable) {
                setEvent(events.homescreen.try_it_out_tapped, {
                  userId: session.user?._id.toString()
                })
              }
              mainTablesSheetRef.current?.present()
            }}
            variant="Medium/Secondary"
            type="On Light"
            maxWidth
            disabled={isLocked}
          >
            Join Main Table
          </DS.Button>
        ) : (
          <DS.Frame style={{ height: 38 }} />
        )}
      </DS.Frame>

      {ENABLE_NOTIFICATIONS && (
        <DS.Frame hidden={type === 'finished' || (type === 'live' && !isLocked)} gap="8">
          <View style={{ height: 38 }} />
          <DS.Button
            style={{
              alignItems: 'center',
              justifyContent: 'center'
            }}
            onPress={() => onReminderPress?.(time?.getDay())}
            variant="Medium/Secondary"
            type="On Light"
            centerContent
            leftIcon={isSetReminderSelected ? <DS.Icon name="Check" color="Global/White" size={16} /> : null}
          >
            {isSetReminderSelected ? 'Reminder is set' : 'Set Reminder'}
          </DS.Button>
        </DS.Frame>
      )}

      <BottomSheetModal
        ref={mainTablesSheetRef}
        index={0}
        overrideReduceMotion={ReduceMotion.System}
        enableDynamicSizing
        maxDynamicContentSize={maxHeight}
        enableDismissOnClose
        enablePanDownToClose
        enableContentPanningGesture={false}
        enableHandlePanningGesture
        detached={false}
        // keyboardBlurBehavior="restore"
        backdropComponent={renderBackdrop}
        backgroundStyle={{
          backgroundColor: DS.palette['Brand/Cream']
        }}
        handleIndicatorStyle={{
          width: 44,
          backgroundColor: `${DS.palette['Brand/Dark Grey']}40`,
          height: 2
        }}
        style={{
          marginHorizontal: isWeb ? 'auto' : undefined,
          maxWidth: maxWidth,
          width: '100%'
        }}
      >
        <BottomSheetScrollView contentContainerStyle={{ paddingBottom: Platform.OS === 'web' ? 100 : 32 }}>
          <DS.Frame gap="16" style={styles.paddingMainGames}>
            <DS.Frame gap="8" row alignItems="center" justifyContent="center">
              <DS.Text variant="Heading 2/Medium">Main Tables</DS.Text>
              {type === 'live' && <LiveBadge />}
            </DS.Frame>
            <DS.Frame gap="8" row style={{ flexWrap: 'wrap' }}>
              {sortBy(advancedTables || [], ({ tableName }) => tableName).map((table, idx) => (
                <AdvancedGameCard
                  key={`card-live-${table.channelName}-${idx}`}
                  actionTime={table.actionTime}
                  blinds={table.blinds}
                  channelName={table.channelName}
                  seats={table.seats}
                  tableName={table.tableName}
                  isLocked={table.isLocked}
                  isFull={table.isFull}
                  onJoin={async (channelOrEmpty) => {
                    if (!session.user) {
                      mainTablesSheetRef.current?.dismiss()
                      return
                    }
                    const userId = session.user?.auth0Id?.toString() || session.user._id
                    const userName = session.user.name || 'Player'
                    const mongoUserId = session.user._id?.toString()
                    if (!mongoUserId) return

                    // Resolve target gameId; let the server tell us if we should
                    // re-enter as observer (returning user previously played).
                    // When we know schedule + slot, always use joinScheduledTable so the backend can
                    // place the user in an open table or create an overflow game when full —
                    // not join a fixed gameId that may already be at maxPlayers.
                    const resolveTarget = async (): Promise<{
                      gameId: Id<'games'>
                      asObserver: boolean
                      viaSchedule: boolean
                    } | null> => {
                      const tableScheduleId = table?.scheduleId || scheduleId
                      const tableTableType = table?.tableType || tableType
                      const tableConfigIndex = table?.tableConfigIndex
                      const tableNumber = table?.tableNumber
                      // Match beginner tables: when we know schedule + slot, always use joinScheduledTable so the
                      // backend can place the user in an open table or create an overflow game when full — not
                      // join a fixed gameId that may already be at maxPlayers.
                      const canJoinViaSchedule =
                        Boolean(tableScheduleId) &&
                        tableTableType != null &&
                        tableConfigIndex !== undefined &&
                        tableNumber !== undefined

                      const isVirtualTable =
                        // biome-ignore format: for merge compare
                        !channelOrEmpty ||
                        channelOrEmpty === '' ||
                        table?.isVirtual ||
                        canJoinViaSchedule

                      if (!isVirtualTable) {
                        // asObserver is server-decided via result.observerMode; no client-side isObserve path remains
                        return { gameId: channelOrEmpty as Id<'games'>, asObserver: false, viaSchedule: false }
                      }
                      if (!tableScheduleId || !tableTableType) {
                        console.error('Missing scheduleId or tableType for virtual table')
                        return null
                      }
                      const result = await joinScheduledTable({
                        scheduleId: tableScheduleId as Id<'gameSchedules'>,
                        userId,
                        userName,
                        mongoUserId,
                        tableType: tableTableType,
                        ...(tableConfigIndex !== undefined &&
                          tableNumber !== undefined && {
                            tableConfigIndex,
                            tableNumber
                          })
                      })
                      return {
                        gameId: result.gameId,
                        asObserver: !!result.observerMode,
                        viaSchedule: true
                      }
                    }

                    try {
                      const target = await resolveTarget()
                      if (!target) return

                      if (target.asObserver) {
                        try {
                          await spectateGameMutation({
                            gameId: target.gameId,
                            userId,
                            name: userName
                          })
                        } catch (observerErr) {
                          const msg = observerErr instanceof Error ? observerErr.message : String(observerErr)
                          // biome-ignore lint/complexity/useSimplifiedLogicExpression: ok as is
                          if (!msg.includes('finished') && !msg.includes('deleted')) throw observerErr
                        }
                      } else if (!target.viaSchedule) {
                        // Legacy: join by known game id only (no unified table slot).
                        await joinGameMutation({
                          gameId: target.gameId,
                          userId,
                          mongoUserId,
                          name: userName
                        })
                      }
                      // Schedule path: joinScheduledTable already runs joinGame on the server.
                      router.navigate({
                        pathname: '/game/[gameId]',
                        params: { gameId: target.gameId }
                      } as any)
                    } catch (error) {
                      console.error('Error joining table:', error)
                      const message = error instanceof Error ? error.message : String(error)
                      if (message.includes('Cannot join - game has finished or been deleted')) {
                        Toast.show({
                          type: 'error',
                          text1: 'This game has finished.'
                        })
                      } else if (message.includes('Game is full')) {
                        Toast.show({
                          type: 'error',
                          text1: 'This game is full.'
                        })
                      } else {
                        Toast.show({
                          type: 'error',
                          text1: 'Failed to join the game. Please try again.'
                        })
                      }
                    } finally {
                      mainTablesSheetRef.current?.dismiss()
                    }
                  }}
                />
              ))}
            </DS.Frame>
          </DS.Frame>
        </BottomSheetScrollView>
      </BottomSheetModal>
    </DS.Frame>
  )
}

const CardLiveGameStyles = StyleSheet.create({
  paddingMainGames: {
    paddingTop: DS.spacing[12],
    ...Platform.select({
      ios: {
        paddingHorizontal: DS.spacing['24']
      },
      android: {
        paddingHorizontal: DS.spacing['16']
      },
      web: {
        paddingHorizontal: DS.spacing['24']
      }
    })
  },
  container: {
    minWidth: 292,
    borderRadius: DS.spacing[16],
    borderWidth: 2,
    paddingVertical: DS.spacing[32],
    paddingHorizontal: DS.spacing[24],
    borderColor: DS.palette['Global/White'],
    ...Platform.select({
      ios: {
        shadowColor: '#000000',
        shadowRadius: 2,
        shadowOffset: { height: 0, width: 2 },
        shadowOpacity: 0.05
      },
      android: {
        elevation: 2
      }
    })
  },
  liveBadge: { position: 'absolute', top: 0, right: DS.spacing[32] }
})
